import type { UserAnswer } from './user-answer.type';
import { pipe } from '../../../../utils/pipe';
import { shuffle } from '../../../../utils/shuffle';
import { uniqueList } from '../../../../utils/unique-list';

export interface State {
  answers: string[];
  userAnswers: UserAnswer[];
  areErrorsVisible: boolean;
  possibleAnswers: string[];
  numberOfAnswersRequired: number;
  complete: boolean;
}

export enum ActionType {
  SetAnswer = 'SET_ANSWER',
  SetErrorsVisible = 'SET_ERRORS_VISIBLE',
  SetPossibleAnswers = 'SET_POSSIBLE_ANSWERS',
  ResetIncorrectAnswers = 'RESET_INCORRECT_ANSWERS',
  Complete = 'COMPLETE',
  Reset = 'RESET',
  ResetAnswers = 'RESET_ANSWERS',
  FillAnswers = 'FILL_ANSWERS',
}

type Action =
  | { type: ActionType.SetAnswer; payload: { key: number; value: UserAnswer } }
  | { type: ActionType.SetErrorsVisible; payload: boolean }
  | { type: ActionType.SetPossibleAnswers; payload: string[] }
  | { type: ActionType.FillAnswers; payload: number }
  | {
      type:
        | ActionType.ResetIncorrectAnswers
        | ActionType.Reset
        | ActionType.ResetAnswers
        | ActionType.Complete;
    };

const initialUserAnswer: UserAnswer = { answer: undefined, correct: false };

export const initialState: State = {
  answers: [],
  userAnswers: [],
  areErrorsVisible: false,
  possibleAnswers: [],
  numberOfAnswersRequired: 0,
  complete: false,
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.SetAnswer: {
      const userAnswers = [...state.userAnswers];
      if (action.payload.value?.answer !== '') {
        userAnswers[action.payload.key] = action.payload.value;
      } else {
        userAnswers[action.payload.key] = { ...initialUserAnswer };
      }
      return { ...state, userAnswers };
    }

    case ActionType.SetErrorsVisible:
      return { ...state, areErrorsVisible: action.payload as boolean };

    case ActionType.SetPossibleAnswers: {
      const possibleAnswers: string[] = pipe(
        uniqueList,
        shuffle
      )(action.payload);

      return {
        ...state,
        answers: action.payload,
        userAnswers: new Array(action.payload?.length ?? 0).fill({
          ...initialUserAnswer,
        }),
        possibleAnswers,
        numberOfAnswersRequired: action.payload?.length ?? 0,
      };
    }

    case ActionType.ResetIncorrectAnswers: {
      const userAnswers = [...state.userAnswers].map((answer) =>
        answer.correct ? answer : { ...initialUserAnswer }
      );

      return {
        ...state,
        userAnswers,
      };
    }

    case ActionType.ResetAnswers:
      return {
        ...state,
        userAnswers: new Array(state.numberOfAnswersRequired).fill({
          ...initialUserAnswer,
        }),
      };

    case ActionType.FillAnswers:
      return {
        ...state,
        userAnswers: state.answers.map((answer, index) => {
          if (index < action.payload) {
            return { answer, correct: true };
          } else {
            return state.userAnswers[index];
          }
        }),
      };

    case ActionType.Complete:
      return { ...state, complete: true };

    case ActionType.Reset:
      return { ...initialState };

    default:
      return state;
  }
};
